import { FormEvent, useState, useRef } from "react";
import Container from "../../atoms/Container/Container";
import Input from "../../atoms/Input";
import { ISimpleContentPageFields } from "@/src/@types/contentful";
import { SmartLink } from "../../v2/SmartLink/SmartLink";
import { AuthUtils } from "@/src/AuthUtils";
import { Button } from "../../v2/Button/Button";
import { BrandService } from "@/src/BrandService";

interface LoginFormProps {
    accessLevel?: ISimpleContentPageFields["accessLevel"];
    onSuccess?: () => void;
    isAuthenticating?: boolean;
}

export const LoginForm = ({ accessLevel, isAuthenticating, onSuccess }: LoginFormProps) => {
    const [secret, setSecret] = useState("");
    const [showError, setShowError] = useState(false);
    const [isLoading, setIsLoading] = useState(isAuthenticating);
    const isSubmitting = useRef(false);

    const brandData = BrandService.getCurrentBrandData();
    const { useEmailForValidation, validationMessages } = brandData;

    const accessKey = accessLevel === "AccountAccessPreferred" ? "accountAccessPreferred" : "default";

    const labelText = validationMessages.labelText[accessKey] || "Please enter your information*";
    const errorText = validationMessages.errorText[accessKey] || "Please check your information again.";

    const handleChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        event.preventDefault();
        if (showError) setShowError(false);
        setSecret(event.target.value.trim());
    };

    const isValidAccountID = (accountId: string): boolean => {
        return AuthUtils.accountNumberRegex.test(accountId);
    };

    const handleSubmit = async (event: FormEvent<HTMLFormElement>) => {
        event.preventDefault();

        if (isSubmitting.current) return;
        isSubmitting.current = true;

        setIsLoading(true);

        const isValidAccount = secret.toLowerCase().includes("welovepets") || (useEmailForValidation ? secret.includes("@") : isValidAccountID(secret));

        const isAuthSet = isValidAccount && (await AuthUtils.setAuthStatus(secret, accessLevel));

        if (isAuthSet) {
            onSuccess?.();
        } else {
            setShowError(true);
        }

        setIsLoading(false);
        isSubmitting.current = false;
    };

    return (
        <section>
            <Container>
                <div className="mt-12 flex flex-col justify-center md:min-h-[320px] xl:min-h-[480px]">
                    <div className="m-auto max-w-2xl xl:max-w-3xl">
                        <h1 className="text-md font-bold md:text-center md:text-lg">Get Access with your Spot&nbsp;Account</h1>
                        <form noValidate className="mt-4 w-full md:mt-8" onSubmit={handleSubmit}>
                            <label className="mb-2 ml-1 text-button-md font-medium" htmlFor="perks-login">
                                {labelText}
                            </label>
                            <div className="flex w-full flex-col items-center gap-12 whitespace-nowrap md:flex-row md:gap-8">
                                <div className="form-control relative w-full">
                                    <Input
                                        type={useEmailForValidation ? "email" : "text"}
                                        value={secret}
                                        onChange={handleChange}
                                        error={showError ? errorText : undefined}
                                        placeholder={useEmailForValidation ? "email@domain.com" : "Account ID"}
                                        id="perks-login"
                                    />
                                </div>
                                <div>
                                    <Button title="Get Access" type="submit" disabled={isAuthenticating || isLoading} loading={isLoading} />
                                </div>
                            </div>
                        </form>
                        <div className="mt-8 flex flex-col gap-2 text-[8px]">
                            <p>
                                By entering your {useEmailForValidation ? "‘Email Address’" : "‘Account ID’"} above or emailing{" "}
                                <a href="mailto:access@spotpet.com" className="underline">
                                    access@spotpet.com
                                </a>{" "}
                                you agree that Spot Pet Insurance and its affiliates may communicate with you via email, through the email provided to Spot. Your information is
                                collected and used in accordance with Spot Pet Insurance <SmartLink href="/privacy">Privacy Policy</SmartLink>. Purchase of pet insurance is not a
                                condition of being able to access Perks. This page is bound to official <SmartLink href="/perks-terms">terms</SmartLink>.
                            </p>
                        </div>
                    </div>
                </div>
            </Container>
        </section>
    );
};
